import * as XLSX from 'xlsx';
import { toPng } from 'html-to-image';

// Utility function to calculate grouped targets
export const calculateGroupedTarget = (groupedTargetValues, type, frequency) => {
  const total = Object.values(groupedTargetValues[type]).reduce((sum, target) => sum + (target || 0), 0);
  return frequency === "Monthly" ? (total / 12).toFixed(2) : total.toFixed(2);
};

// Excel download functionality
export const handleDownloadExcel = (year, frequency, month) => {
  const table = document.getElementById("scorecard-table");
  if (!table) return;

  // Clone the table to avoid modifying the DOM
  const tableClone = table.cloneNode(true);

  // Find all rows (skip header)
  const rows = tableClone.querySelectorAll("tbody tr");
  rows.forEach(row => {
    // Find the last cell (assumed to be percentage diff)
    const cells = row.querySelectorAll("td");
    if (cells.length > 0) {
      const lastCell = cells[cells.length - 1];
      let val = lastCell.textContent || lastCell.innerText;
      // Remove any % sign and whitespace
      if (typeof val === 'string' && val.trim().endsWith('%')) {
        val = val.replace('%', '').trim();
      }
      const num = parseFloat(val);
      if (!isNaN(num)) {
        // Always multiply by 100 and append % to match the table
        const newVal = (num * 100).toFixed(2) + '%';
        lastCell.textContent = newVal;
      }
    }
  });

  const wb = XLSX.utils.table_to_book(tableClone, { sheet: "Scorecard" });
  XLSX.writeFile(wb, `Scorecard_${year}_${frequency}${frequency === "Monthly" ? `_${month}` : ""}.xlsx`);
};

// Chart and table image download functionality
export const handleDownloadChartAndTableImage = async (showAllUnits, year) => {
  const chartId = showAllUnits ? 'all-units-chart' : 'single-unit-chart';
  const tableId = showAllUnits ? 'all-units-table' : 'single-unit-table';
  const chartNode = document.getElementById(chartId);
  const tableNode = document.getElementById(tableId);

  if (!chartNode || !tableNode) return;

  const container = document.createElement('div');
  container.style.background = '#fff';
  container.style.padding = '24px';
  container.style.display = 'inline-block';

  // Clone and append chart
  const chartClone = chartNode.cloneNode(true);
  chartClone.style.display = 'block';
  // Copy canvas content
  const origCanvases = chartNode.querySelectorAll('canvas');
  const cloneCanvases = chartClone.querySelectorAll('canvas');
  origCanvases.forEach((origCanvas, idx) => {
    const cloneCanvas = cloneCanvases[idx];
    if (cloneCanvas && origCanvas) {
      cloneCanvas.width = origCanvas.width;
      cloneCanvas.height = origCanvas.height;
      const ctx = cloneCanvas.getContext('2d');
      ctx.drawImage(origCanvas, 0, 0);
    }
  });
  container.appendChild(chartClone);

  // Add some spacing
  const spacer = document.createElement('div');
  spacer.style.height = '24px';
  container.appendChild(spacer);

  // Clone and append table
  const tableClone = tableNode.cloneNode(true);
  tableClone.style.display = 'block';
  container.appendChild(tableClone);

  document.body.appendChild(container);

  try {
    const dataUrl = await toPng(container, { backgroundColor: '#fff' });
    const link = document.createElement('a');
    link.download = showAllUnits ? `All_Units_Scorecard_${year}.png` : `Unit_Scorecard_${year}.png`;
    link.href = dataUrl;
    link.click();
  } catch (err) {
    console.error('Failed to export image', err);
  } finally {
    document.body.removeChild(container);
  }
};

// Calculate field targets
export const calculateFieldTargets = (assignedFields, baseYearData, selectedYear) => {
  const targets = {};
  const fieldStatusMapping = {};
  
  // Calculate base year from the selected year
  const baseYear = String(Number(selectedYear) - 1);

  const getBaseValue = (field) => {
    const allDatasets = ["water", "waste", "emission", "energy", "governance", "social"];
    
    for (const dataset of allDatasets) {
      const datasetItems = baseYearData[dataset] || [];
      
      // Filter by field AND year, handling both daily and monthly data
      const filteredItems = datasetItems.filter(item => {
        // Must match the field
        if (String(item.fieldId) !== String(field.fieldId)) return false;
        
        // Get the item's year
        let itemYear;
        if (item.frequencyType === "Daily" && item.frequencyDate) {
          // Daily data: extract year from date
          itemYear = new Date(item.frequencyDate).getFullYear().toString();
        } else if (item.frequencyYear) {
          // Monthly data: use frequencyYear
          itemYear = item.frequencyYear;
        } else if (item.year) {
          // Governance/Social data: use year field
          itemYear = String(item.year);
        } else {
          // Skip items without year info
          return false;
        }
        
        // Only include data from the base year
        return itemYear === baseYear;
      });
      
      // Sum the filtered items
      const total = filteredItems.reduce((sum, item) => {
        const value = Number(item.prevValue || item.value || 0);
        return sum + value;
      }, 0);
      
      if (total > 0) {
        console.log(`✅ Base value for field ${field.fieldId}: ${total} (from ${filteredItems.length} items in ${baseYear})`);
        return total;
      }
    }
   
    return null;
  };

  // Rest of the target calculation logic
  for (const field of assignedFields) {
    const baseValue = getBaseValue(field);
    const rawTarget = Number(field.target);
    const targetType = (field.targetType || "").toLowerCase();
    let finalTarget;

    let fieldName = null;
    const allDatasets = ["water", "waste", "emission", "energy", "governance", "social"];
    for (const dataset of allDatasets) {
      const datasetItems = baseYearData[dataset] || [];
      const match = datasetItems.find(i => String(i.fieldId) === String(field.fieldId));
      if (match && match.fieldName) {
        fieldName = match.fieldName;
        break;
      }
    }

    if (fieldName === "Percentage of Renewable Energy") {
      finalTarget = targetType === "increase"
        ? +(baseValue + (baseValue * 0.40)).toFixed(2)
        : +(baseValue - rawTarget).toFixed(2);
    } else if (fieldName === "Waste Recycling Rate") {
      finalTarget = targetType === "increase"
        ? +(baseValue - (baseValue * 0.02)).toFixed(2)
        : +(baseValue - rawTarget).toFixed(2);
    }
    else if (["reduction", "increase"].includes(targetType)) {
      const rate = rawTarget / 100;
      finalTarget = targetType === "reduction"
        ? +(baseValue * (1 - rate)).toFixed(2)
        : +(baseValue * (1 + rate)).toFixed(2);
    } else {
      finalTarget = rawTarget.toFixed ? rawTarget.toFixed(2) : "N/A";
    }

    if (
      (field.theme === "Social" || field.theme === "Governance") &&
      ["reduction", "increase"].includes(targetType)
    ) {
      if (field.target === null || field.target === undefined || Number(field.target) === 0) {
        finalTarget = 0;
      } else if (baseValue === null || baseValue === 0 || isNaN(rawTarget)) {
        finalTarget = "N/A";
      } else {
        const rate = rawTarget / 100;
        finalTarget = targetType === "reduction"
          ? +(baseValue * (1 - rate)).toFixed(2)
          : +(baseValue * (1 + rate)).toFixed(2);
      }
    }

    targets[field.fieldId] = finalTarget;
    fieldStatusMapping[field.fieldId] = field.scorecardStatus;
  }

  // Handle social gender diversity calculations
  const socialBase = baseYearData.social || [];
  const femaleGroup = socialBase.filter(i => i.fieldGroup === "A_B_B");
  const maleGroup = socialBase.filter(i => i.fieldGroup === "A_B_A");
  const femaleBaseTotal = femaleGroup.reduce((sum, i) => sum + Number(i.value || 0), 0);
  const maleBaseTotal = maleGroup.reduce((sum, i) => sum + Number(i.value || 0), 0);
  const total = femaleBaseTotal + maleBaseTotal;

  if (total > 0) {
    const femaleBasePercent = (femaleBaseTotal / total) * 100;
    const maleBasePercent = (maleBaseTotal / total) * 100;
    const genderMap = {};
    [...femaleGroup, ...maleGroup].forEach(i => {
      genderMap[i.fieldId] = i.fieldGroup;
    });

    for (const field of assignedFields) {
      const group = genderMap[field.fieldId];
      if (!["A_B_B", "A_B_A"].includes(group)) continue;

      const isFemale = group === "A_B_B";
      const basePercent = isFemale ? femaleBasePercent : maleBasePercent;
      const rawTarget = Number(field.target);
      const targetType = (field.targetType || "").toLowerCase();
      let finalTarget;

      if (basePercent === 0 || isNaN(rawTarget)) {
        finalTarget = "N/A";
      } else {
        finalTarget = targetType === "reduction"
          ? +(basePercent - rawTarget).toFixed(2)
          : targetType === "increase"
          ? +(basePercent + rawTarget).toFixed(2)
          : rawTarget;
      }

      targets[field.fieldId] = finalTarget;
      fieldStatusMapping[field.fieldId] = field.scorecardStatus;
    }
  }

  return { targets, fieldStatusMapping };
};

// Aggregate yearly data
export const aggregateYearlyData = (data, targetYear) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];
  
  const strTargetYear = String(targetYear);
  
  // Filter data for the target year, handling both daily and monthly data
  const yearData = data.filter(item => {
    let itemYear;
    
    // Handle daily data
    if (item.frequencyType === "Daily" && item.frequencyDate) {
      itemYear = new Date(item.frequencyDate).getFullYear().toString();
    } 
    // Handle monthly/yearly data
    else if (item.frequencyYear) {
      itemYear = item.frequencyYear;
    } else {
      return false;
    }
    
    return itemYear === strTargetYear;
  });
  
  // Group by fieldId and fieldGroup, aggregating values
  const groupedData = yearData.reduce((acc, item) => {
    const key = `${item.fieldId}-${item.fieldGroup || 'default'}`;
    
    const itemValue = Number(item.prevValue || item.value || 0);
    
    if (!acc[key]) {
      acc[key] = {
        ...item,
        prevValue: itemValue,
        aggregatedCount: 1
      };
    } else {
      acc[key].prevValue += itemValue;
      acc[key].aggregatedCount++;
    }
    
    return acc;
  }, {});
  
  return Object.values(groupedData);
};

// API call functions
export const fetchScorecardAssignedFields = async (companyId, token, companyUnit, year, setTargetValues, setGroupedTargetValues, setStatusSelections) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}assign-companies/assignFields/scorecard/${companyId}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    const result = await response.json();
    if (!result.status) return [];

    const assignedFields = result.data;
    const fieldIds = assignedFields.map(f => f.fieldId);
    const baseYear = String(Number(year) - 1);

    // Fetch base year data
    const baseDataResponse = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        fieldIds,
        companyUnit,
        userId: companyId,
        selectedYear: baseYear,
      }),
    });

    const baseYearResult = await baseDataResponse.json();
    const baseYearData = baseYearResult.data || {};

    // Fetch governance base data
    const governanceBaseResponse = await fetch(`${process.env.REACT_APP_BASE_URL}governance/getGovernanceData`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kpiId: assignedFields.map(f => f.kpiId),
        catId: assignedFields.map(f => f.catId),
        subCatId: assignedFields.map(f => f.subCatId),
        fieldId: assignedFields.map(f => f.fieldId),
        companyId: assignedFields.map(f => f.companyId),
        companyUnit: [companyUnit],
        year: [baseYear],
      }),
    });
    const governanceBaseResult = await governanceBaseResponse.json();
    baseYearData.governance = governanceBaseResult.data || [];

    // Fetch social base data
    const socialBaseResponse = await fetch(`${process.env.REACT_APP_BASE_URL}social/getSocialData`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kpiId: assignedFields.map(f => f.kpiId),
        catId: assignedFields.map(f => f.catId),
        subCatId: assignedFields.map(f => f.subCatId),
        fieldId: assignedFields.map(f => f.fieldId),
        companyId: assignedFields.map(f => f.companyId),
        companyUnit: [companyUnit],
        year: [baseYear],
      }),
    });
    const socialBaseResult = await socialBaseResponse.json();
    baseYearData.social = socialBaseResult.data || [];

    const { targets, fieldStatusMapping } = calculateFieldTargets(assignedFields, baseYearData, year);
    
    const groupedTargetValues = {
      water: { total: (baseYearData.water || []).filter(item => item.fieldGroup === "A" && item.prevValue !== null && !isNaN(item.prevValue)).reduce((sum, item) => sum + Number(item.prevValue), 0) * 0.98 },
      waste: { total: (baseYearData.waste || []).filter(item => ["B1", "B2", "B3"].includes(item.fieldGroup) && item.prevValue !== null && !isNaN(item.prevValue)).reduce((sum, item) => sum + Number(item.prevValue), 0) * 1.02 },
    };

    // Set the state values if setters are provided
    if (setTargetValues) setTargetValues(targets);
    if (setGroupedTargetValues) setGroupedTargetValues(groupedTargetValues);
    if (setStatusSelections) setStatusSelections(fieldStatusMapping);

    return assignedFields;
  } catch (error) {
    console.error("❌ Error in fetchScorecardAssignedFields:", error);
    return [];
  }
};

export const fetchAllScorecardData = async (fieldIds, companyUnit, userId, selectedYear, token, setScorecardData) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}scorecard/fetch-all`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ fieldIds, companyUnit, userId, selectedYear }),
    });

    const result = await response.json();

    if (result.data && typeof result.data === 'object') {
      const newScorecardData = result.data;
      const groupedData = { water: {}, waste: {} };

      newScorecardData.water.forEach(item => {
        if (item.fieldGroup === "A") {
          let key;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            key = `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`;
          } else if (item.frequencyType === "Monthly" && item.frequencyMonth && item.frequencyYear) {
            key = `${item.frequencyMonth}-${item.frequencyYear}`;
          }
          if (key) groupedData.water[key] = (groupedData.water[key] || 0) + (item.prevValue || 0);
        }
      });

      newScorecardData.waste.forEach(item => {
        if (["B1", "B2", "B3"].includes(item.fieldGroup)) {
          let key;
          if (item.frequencyType === "Daily" && item.frequencyDate) {
            const date = new Date(item.frequencyDate);
            key = `${date.toLocaleString('default', { month: 'long' })}-${date.getFullYear()}`;
          } else if (item.frequencyType === "Monthly" && item.frequencyMonth && item.frequencyYear) {
            key = `${item.frequencyMonth}-${item.frequencyYear}`;
          }
          if (key) groupedData.waste[key] = (groupedData.waste[key] || 0) + (item.prevValue || 0);
        }
      });

      newScorecardData.groupedWater = groupedData.water;
      newScorecardData.groupedWaste = groupedData.waste;

      setScorecardData(newScorecardData);
    } else {
      setScorecardData({ water: [], waste: [], emission: [], energy: [] });
    }
  } catch (error) {
    console.error("Error fetching scorecard data:", error);
    setScorecardData({ water: [], waste: [], emission: [], energy: [] });
  }
};

export const fetchGovernanceData = async (kpiIds, catIds, subCatIds, fieldIds, companyIds, companyUnits, selectedYear, token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}governance/getGovernanceData`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kpiId: kpiIds,
        catId: catIds,
        subCatId: subCatIds,
        fieldId: fieldIds,
        companyId: companyIds,
        companyUnit: companyUnits,
        year: [selectedYear],
      }),
    });

    const result = await response.json();
    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error("Error fetching governance data:", error);
    return [];
  }
};

export const fetchSocialData = async (kpiIds, catIds, subCatIds, fieldIds, companyIds, companyUnits, selectedYear, token) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_BASE_URL}social/getSocialData`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kpiId: kpiIds,
        catId: catIds,
        subCatId: subCatIds,
        fieldId: fieldIds,
        companyId: companyIds,
        companyUnit: companyUnits,
        year: [selectedYear],
      }),
    });

    const result = await response.json();
    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error("Error fetching social data:", error);
    return [];
  }
};
